import { SellerRfqTemplate as template } from './seller-rfq.template';
import { SellerRfqStyles as styles } from './seller-rfq.styles';
import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { RfqListing } from '../../components/page-specific/seller-rfq-components/rfq-listing/rfq-listing';
import { IRfq, Project } from '../../types';
import {
  BasketListing,
  IBasketGrid,
} from '../../components/page-specific/seller-rfq-components/basket-listing/basket-listing';
import { IStructuredChatService } from '../../services/structured-chat.service';
import { VppaModal } from '../../components/modals/vppa-modal/vppa-modal';
import { ProjectRfqsGraph } from '../../components/page-specific/seller-rfq-components/project-rfqs-graph/project-rfqs-graph';
import { IProjectService } from '../../services';
import { Tabs } from '@microsoft/fast-foundation';
import { IAuthService } from '../../services/auth.service';
import { PRODUCT_TYPE } from '../../utils';

const name = 'seller-rfq';

@customElement({
  name,
  template,
  styles,
})
export class SellerRfq extends FASTElement {
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @IProjectService projectService: IProjectService;
  @IAuthService authService: IAuthService;

  @observable criteria: string = '';

  @observable rfqListingElement: RfqListing;
  @observable basketListingElement: BasketListing;
  public vppaModal: VppaModal;

  @observable sellerRfqTabs: Tabs;
  @observable isMyProjectsTabSelected: boolean = true;
  @observable projectSelected: Project;
  @observable rfqList: Array<IRfq> = [];

  @observable selectedRfq: IRfq = null;
  @observable selectedVppa: string = null;
  @observable projectRfqsGraphRef: ProjectRfqsGraph;
  @observable fullTabRef: Tabs;

  connectedCallback(): void {
    super.connectedCallback();
  }

  criteriaChanged(oldValue, newValue) {
    this.rfqListingElement?.setRfqListingCriteria(newValue);
  }

  public projectSelection(project: Project) {
    this.rfqListingElement.clearSelectedRfqs();
    this.projectSelected = project;
    this.rfqListingElement.setAllowRfqSelection(project?.PROJECT_ID ? true : false);
    this.basketListingElement.clearBasketStatus();
    this.updateBasketGrid();
  }

  public rfqSelected(rfqList: Array<IRfq>) {
    this.rfqList = rfqList;
    this.projectRfqsGraphRef.rfqsSelected(rfqList);
    this.updateBasketGrid();
  }

  public vppaSelected(vppaId: string) {
    this.selectedVppa = vppaId;
  }

  public proposeVppa(rfqId: IRfq) {
    this.selectedRfq = rfqId;
  }

  public cancelVppaSelection() {
    this.selectedRfq = null;
    this.selectedVppa = null;
    this.basketListingElement.removeSelection();
  }

  public openStructuredChat() {
    this.structuredChatService.openStructuredChat({
      selectedVPPAIDs: [this.selectedVppa],
      selectedRFQIDs: [this.selectedRfq.RFQ_ID],
      selectedNegotiationID: '',
    });
  }

  public proposalCreated() {
    this.selectedVppa = null;
    this.basketListingElement.getNextElement(this.selectedRfq.RFQ_ID);
  }

  public openVppaModal() {
    this.vppaModal.openModal(false, '', this.projectSelected.PROJECT_ID);
  }

  public rfqListing() {
    this.isMyProjectsTabSelected = false;
  }

  public myProjects() {
    this.isMyProjectsTabSelected = true;
  }
  public downloadBasketCsv() {
    this.basketListingElement.downloadBasketCsv();
  }

  public async updateBasketGrid() {
    const basketList: Array<IBasketGrid> = [];
    let project: IBasketGrid;
    let volume: number = 0;
    let impliedCapacityFactor: number = null;
    let impliedCarbonVolume: number = 0;
    let volumeYear: number = 0;
    let fixedPriceRange: number = 0;
    let startDate: number = 0;
    let tenorMonths: number = 0;
    let impliedCarbonPrice = 0;
    let collateral = 0;
    const basketStatistics: IBasketGrid = {
      PROJECT_NAME: 'Basket Statistics',
      TICKER: '',
      UNIQUE_ID: '',
      TECHNOLOGY: '',
      ISO: '',
      SETTLEMENT_POINT: '',
      VOLUME_YEAR: null,
      VOLUME: null,
      FIXED_PRICE_LOWER_BOUND: null,
      FIXED_PRICE_UPPER_BOUND: null,
      START_DATE: null,
      TENOR_MONTHS: null,
      COLLATERAL: null,
      IMPLIED_CARBON_PRICE_MIN: null,
      IMPLIED_CARBON_PRICE_MAX: null,
      STATUS: '',
      PROJECT_OPERATIONAL_STATUS: null,
      PROJECT_ID: '',
      RFQ_ID: '',
      IS_STATISTICS: true,
      PRODUCT_TYPE: '',
      CONTRACT_TYPE: '',
      RFQ_CREDIT_PROFILE: '',
    };

    if (this.projectSelected) {
      project = {
        PROJECT_NAME: this.projectSelected.NAME,
        TICKER: null,
        UNIQUE_ID: '',
        TECHNOLOGY: this.projectSelected.TECHNOLOGY,
        ISO: this.projectSelected.ISO,
        SETTLEMENT_POINT: this.projectSelected.SETTLEMENT_POINT,
        VOLUME_YEAR: null,
        VOLUME: this.projectSelected.AVAILABLE_CAPACITY_AC,
        FIXED_PRICE_LOWER_BOUND: null,
        FIXED_PRICE_UPPER_BOUND: null,
        START_DATE: null,
        TENOR_MONTHS: null,
        COLLATERAL: null,
        IMPLIED_CARBON_PRICE_MIN: null,
        IMPLIED_CARBON_PRICE_MAX: null,
        STATUS: this.projectSelected.PROJECT_STATUS,
        PROJECT_OPERATIONAL_STATUS: this.projectSelected.PROJECT_OPERATIONAL_STATUS,
        PROJECT_ID: this.projectSelected.PROJECT_ID,
        RFQ_ID: null,
        IS_STATISTICS: false,
        PRODUCT_TYPE: '',
        CONTRACT_TYPE: '',
        RFQ_CREDIT_PROFILE: '',
      };
    }

    if (this.rfqList.length > 0) {
      const hoursOnYear = 8760;
      await this.projectService.getProjectKeyMetrics(project.PROJECT_ID).then((result) => {
        impliedCapacityFactor = Number(result?.REPLY[0]?.IMPLIED_CAPACITY_FACTOR.toFixed(2));
      });
      this.rfqList.forEach((rfq) => {
        const volumeAux =
          impliedCapacityFactor && impliedCapacityFactor != 0
            ? rfq.REQUESTED_VOLUME / (hoursOnYear * impliedCapacityFactor)
            : null;
        volume += volumeAux;
        volumeYear += rfq.REQUESTED_VOLUME;
        if (rfq.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          fixedPriceRange += rfq.FIXED_PRICE * rfq.REQUESTED_VOLUME;
        } else {
          fixedPriceRange +=
            ((rfq.FIXED_PRICE_LOWER_BOUND + rfq.FIXED_PRICE_UPPER_BOUND) / 2) *
            rfq.REQUESTED_VOLUME;
        }
        startDate += parseInt(rfq.TENOR_START_DATE.toString());
        tenorMonths += rfq.TENOR_MONTHS * rfq.REQUESTED_VOLUME;
        if (rfq.IMPLIED_CARBON_PRICE_MIN && rfq.IMPLIED_CARBON_PRICE_MAX) {
          impliedCarbonVolume += rfq.REQUESTED_VOLUME;
          impliedCarbonPrice +=
            ((rfq.IMPLIED_CARBON_PRICE_MIN + rfq.IMPLIED_CARBON_PRICE_MAX) / 2) *
            rfq.REQUESTED_VOLUME;
        }
        collateral += rfq.BUYER_POST_COD_COLLATERAL;
        basketList.push({
          PROJECT_NAME: null,
          PROJECT_OPERATIONAL_STATUS: null,
          TICKER: rfq.TICKER,
          UNIQUE_ID: rfq.UNIQUE_ID,
          TECHNOLOGY: rfq.TECHNOLOGY,
          ISO: rfq.ISO,
          SETTLEMENT_POINT: rfq.SETTLEMENT_POINT,
          VOLUME_YEAR: rfq.REQUESTED_VOLUME,
          VOLUME: volumeAux,
          FIXED_PRICE_LOWER_BOUND:
            rfq.PRODUCT_TYPE == 'RECS' ? rfq.FIXED_PRICE : rfq.FIXED_PRICE_LOWER_BOUND,
          FIXED_PRICE_UPPER_BOUND:
            rfq.PRODUCT_TYPE == 'RECS' ? rfq.FIXED_PRICE : rfq.FIXED_PRICE_UPPER_BOUND,
          START_DATE: rfq.TENOR_START_DATE,
          TENOR_MONTHS: rfq.TENOR_MONTHS,
          COLLATERAL: rfq.BUYER_POST_COD_COLLATERAL,
          RFQ_CREDIT_PROFILE: rfq.RFQ_CREDIT_PROFILE,
          IMPLIED_CARBON_PRICE_MIN:
            rfq.IMPLIED_CARBON_PRICE_MIN && rfq.IMPLIED_CARBON_PRICE_MAX
              ? rfq.IMPLIED_CARBON_PRICE_MIN
              : null,
          IMPLIED_CARBON_PRICE_MAX:
            rfq.IMPLIED_CARBON_PRICE_MIN && rfq.IMPLIED_CARBON_PRICE_MAX
              ? rfq.IMPLIED_CARBON_PRICE_MAX
              : null,
          STATUS: rfq.RFQ_STATUS,
          PROJECT_ID: null,
          RFQ_ID: rfq.RFQ_ID,
          IS_STATISTICS: false,
          PRODUCT_TYPE: rfq.PRODUCT_TYPE,
          CONTRACT_TYPE: rfq.CONTRACT_TYPE,
        });
      });
      basketStatistics.VOLUME = volume;
      basketStatistics.FIXED_PRICE_LOWER_BOUND = fixedPriceRange / volumeYear;
      basketStatistics.START_DATE = parseInt((startDate / this.rfqList.length).toFixed(0));
      basketStatistics.TENOR_MONTHS = tenorMonths / volumeYear;
      basketStatistics.IMPLIED_CARBON_PRICE_MIN = impliedCarbonPrice / impliedCarbonVolume;
      basketStatistics.COLLATERAL = collateral / this.rfqList.length;
    }
    this.basketListingElement.setBasketData(basketList, project, basketStatistics);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }
}
